import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button, Checkbox, Icon, Label, Message } from 'semantic-ui-react';
import moment from 'moment';

import {
    ARCHIVE_PERSON_DOCUMENT,
    SET_PERSON_REGISTRATION,
} from '../../../../mutations/PERSON_REGISTRATION';
import {
    usePersonDocumentUpload,
    type PendingDocument,
} from '../../../../hooks/usePersonDocumentUpload';
import {
    HEALTH_INFORMATION_MAX_LENGTH,
    PERSON_DOCUMENT_ACCEPT,
    PERSON_DOCUMENT_KIND_OPTIONS,
} from '../../../../utils/registration';

import styles from './RegistrationSections.module.css';

/**
 * The registration half of a person's profile: what they consented to, who consented on their
 * behalf, and the paperwork behind it.
 *
 * **Neither of these components checks a role.** `user.registration` arrives null and
 * `user.supportingDocuments` empty for a viewer the server will not show them to, so rendering
 * what came back *is* the gate. The same construction the galleries and streams pages use —
 * one answer rather than a client-side guess that can disagree with the server.
 *
 * This is also the "existing person" path from the brief: people already in Joyya are
 * re-registered from their profile, and land in the same row as someone created on the new form.
 */

/**
 * The five answers the paper notice and consent form actually asks for, in its own groups.
 *
 * The three data-use rows that used to be here (`consentDataAggregate` / `Reporting` /
 * `Programs`) were retired on 2026-10-13: the paper form never split data use three ways, so
 * showing them back implied a distinction nobody was ever asked to make. Their columns remain
 * and are derived from the single consent — see `backend/src/db/PersonRegistration.ts`.
 *
 * Wording is kept identical to `ConsentSection.tsx` on purpose: somebody reading a profile and
 * somebody filling the form in must see the same sentence, or the record looks like it answers
 * a different question from the one that was put.
 */
const CONSENT_GROUPS: Array<{ title: string; fields: Array<{ key: string; label: string }> }> = [
    {
        title: 'Personal Information',
        fields: [
            { key: 'consentDataCollection', label: 'Collection and use of their information in reporting, social media, running programs' },
            // **Picture permission is deliberately not here.** It lives on `Membership`, not
            // `PersonRegistration` — every key in this list is read off the registration row
            // and written back through `setPersonRegistration`, which has no such argument.
            // Listing it would show a permanent ✗ regardless of the real answer, and the edit
            // would fail. The profile already displays it from `user.picturePermission`
            // (AdminProfileView.tsx:415, TeacherProfileView.tsx:102).
        ],
    },
    {
        title: 'Health and Safety',
        fields: [
            { key: 'healthSafetyBriefed', label: 'Briefed on health and safety' },
            { key: 'consentHealthSafety', label: 'Consents to the health and safety risks of their program' },
        ],
    },
    {
        title: 'ID',
        fields: [
            { key: 'idSightedByCM', label: 'ID sighted by the Centre Manager' },
        ],
    },
];

const GUARDIAN_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'guardianConsentsToChildData', label: "Parent consents to use of the child's data" },
    { key: 'guardianIdSightedConfirmsRelationship', label: "Parent's ID sighted, confirms relationship" },
    { key: 'guardianNoIdButConfirmed', label: 'No parent ID, confirmed to the Centre Manager’s satisfaction' },
    { key: 'guardianIsMinorNoAlternative', label: 'Parent is a minor, no adult guardian available' },
];

/**
 * A text field that saves when you leave it, and only if it changed.
 *
 * Seeded from the server value and re-seeded when that changes (after a save refetches), so what
 * is shown is always what is stored once you have moved on. The same rhythm as the personal-detail
 * fields above it on the profile.
 */
const BlurField = ({
    id,
    label,
    value,
    onCommit,
    disabled,
    type = 'text',
    placeholder,
    multiline = false,
    maxLength,
}: {
    id: string;
    label: string;
    value: string | null | undefined;
    onCommit: (next: string) => void;
    disabled: boolean;
    type?: 'text' | 'date';
    placeholder?: string;
    multiline?: boolean;
    maxLength?: number;
}) => {
    const [draft, setDraft] = useState(value ?? '');
    useEffect(() => setDraft(value ?? ''), [value]);

    const commitIfChanged = () => {
        if (draft !== (value ?? '')) onCommit(draft);
    };

    return (
        <div className={styles.fieldRow}>
            <label htmlFor={id} className={styles.fieldLabel}>{label}</label>
            {multiline ? (
                <>
                    <textarea
                        id={id}
                        rows={4}
                        maxLength={maxLength}
                        disabled={disabled}
                        placeholder={placeholder}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commitIfChanged}
                    />
                    {maxLength != null && <div className={styles.fieldCounter}>{draft.length} / {maxLength}</div>}
                </>
            ) : (
                <input
                    id={id}
                    type={type}
                    disabled={disabled}
                    placeholder={placeholder}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitIfChanged}
                />
            )}
        </div>
    );
};

/** "Name — relationship — phone", skipping whatever is blank. */
const contactLine = (...parts: Array<string | null | undefined | false>) =>
    parts.filter((p): p is string => typeof p === 'string' && p.trim() !== '').join(' — ');

const Tick = ({ on }: { on: boolean }) => (
    <span className={on ? styles.tickOn : styles.tickOff}>{on ? '✓' : '✗'}</span>
);

export const ConsentSectionCard = ({
    registration,
    personId,
    canEdit,
    onRefetch,
}: {
    registration: any | null;
    personId: number;
    canEdit: boolean;
    onRefetch: () => void;
}) => {
    const [setRegistration, { loading }] = useMutation(SET_PERSON_REGISTRATION);
    const [error, setError] = useState('');

    // Someone who has never been through the new process has no row. Still render the section
    // in edit mode — recording that they have been through it is the whole point of this path.
    if (registration == null && !canEdit) {
        return null;
    }

    const commit = async (field: string, next: boolean | string) => {
        setError('');
        try {
            await setRegistration({
                variables: {
                    personId,
                    [field]: next,
                    // Ticking anything here means this person has been taken through the new
                    // registration. The server only stamps `completedAt` if it is not already
                    // set, so re-saving later never moves the original date.
                    registrationCompleted: true,
                },
            });
            onRefetch();
        } catch (e: any) {
            setError(e?.message || 'Could not save that.');
        }
    };

    const hasGuardian = registration?.guardianName
        || GUARDIAN_FIELDS.some(({ key }) => registration?.[key]);

    const hasEmergencyContact = !!(registration?.emergencyContactName || registration?.emergencyContactPhone);

    // The server answers both halves: whether this viewer may see health information at all
    // (admins and social workers — not centre managers), and what is recorded. Hidden entirely when
    // they may not, rather than shown empty, so nobody is invited to type over what they cannot see.
    const canSeeHealth = !!registration?.canSeeHealthInformation;

    return (
        <>
            {registration?.completedAt && (
                <div className={styles.completedLine}>
                    New registration completed {moment(registration.completedAt).format('MMMM Do Y')}
                </div>
            )}

            {/* Grouped under the paper form's own headings, matching the registration form.
                Flat, the five ran together and the health & safety pair read as one answer. */}
            <div className={styles.consentGrid}>
                {CONSENT_GROUPS.map(({ title, fields }) => (
                    <div className={styles.consentGroup} key={title}>
                        <div className={styles.consentGroupTitle}>{title}</div>
                        {fields.map(({ key, label }) => (
                            <div className={styles.consentRow} key={key}>
                                {canEdit ? (
                                    <Checkbox
                                        label={label}
                                        disabled={loading}
                                        checked={!!registration?.[key]}
                                        onChange={() => commit(key, !registration?.[key])}
                                    />
                                ) : (
                                    <>
                                        <Tick on={!!registration?.[key]} />
                                        <span>{label}</span>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {(hasGuardian || canEdit) && (
                <>
                    <div className={styles.subTitle}>Parent or guardian</div>
                    {canEdit ? (
                        // Were display-only until 2026-11-03, so re-registering an existing person
                        // could tick the guardian's consents but never record who the guardian was.
                        <div className={styles.fieldGrid}>
                            <BlurField id="profile-guardian-name" label="Name" disabled={loading}
                                value={registration?.guardianName} onCommit={(v) => commit('guardianName', v)} />
                            <BlurField id="profile-guardian-dob" label="Date of birth" type="date" disabled={loading}
                                value={registration?.guardianDob} onCommit={(v) => commit('guardianDob', v)} />
                            <BlurField id="profile-guardian-relationship" label="Relationship" disabled={loading}
                                value={registration?.guardianRelationship} onCommit={(v) => commit('guardianRelationship', v)} />
                            <BlurField id="profile-guardian-phone" label="Phone" disabled={loading}
                                value={registration?.guardianPhone} onCommit={(v) => commit('guardianPhone', v)} />
                        </div>
                    ) : registration?.guardianName && (
                        <div className={styles.guardianLine}>
                            {contactLine(
                                registration.guardianName,
                                registration.guardianRelationship,
                                registration.guardianDob && `born ${moment(registration.guardianDob, 'YYYY-MM-DD').format('MMMM Do Y')}`,
                                registration.guardianPhone,
                            )}
                        </div>
                    )}
                    <div className={styles.consentGrid}>
                        {GUARDIAN_FIELDS.map(({ key, label }) => (
                            <div className={styles.consentRow} key={key}>
                                {canEdit ? (
                                    <Checkbox
                                        label={label}
                                        disabled={loading}
                                        checked={!!registration?.[key]}
                                        onChange={() => commit(key, !registration?.[key])}
                                    />
                                ) : (
                                    <>
                                        <Tick on={!!registration?.[key]} />
                                        <span>{label}</span>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {(hasEmergencyContact || canEdit) && (
                <>
                    <div className={styles.subTitle}>Emergency contact</div>
                    {canEdit ? (
                        <div className={styles.fieldGrid}>
                            <BlurField id="profile-emergency-name" label="Name" disabled={loading}
                                value={registration?.emergencyContactName} onCommit={(v) => commit('emergencyContactName', v)} />
                            <BlurField id="profile-emergency-phone" label="Number" disabled={loading}
                                value={registration?.emergencyContactPhone} onCommit={(v) => commit('emergencyContactPhone', v)} />
                            <BlurField id="profile-emergency-relationship" label="Relationship" disabled={loading}
                                value={registration?.emergencyContactRelationship} onCommit={(v) => commit('emergencyContactRelationship', v)} />
                        </div>
                    ) : (
                        <div className={styles.guardianLine}>
                            {contactLine(
                                registration?.emergencyContactName,
                                registration?.emergencyContactRelationship,
                                registration?.emergencyContactPhone,
                            )}
                        </div>
                    )}
                </>
            )}

            {canSeeHealth && (registration?.healthInformation || canEdit) && (
                <>
                    <div className={styles.subTitle}>Health information</div>
                    {canEdit ? (
                        <BlurField id="profile-health" label="Health information they have shared" multiline
                            maxLength={HEALTH_INFORMATION_MAX_LENGTH} disabled={loading}
                            value={registration?.healthInformation} onCommit={(v) => commit('healthInformation', v)} />
                    ) : (
                        <div className={styles.healthText}>{registration?.healthInformation}</div>
                    )}
                </>
            )}

            {error && <Message error visible size="tiny" content={error} />}
        </>
    );
};

const kindLabel = (kind: string) =>
    PERSON_DOCUMENT_KIND_OPTIONS.find((o) => o.value === kind)?.text ?? kind;

export const SupportingDocumentsCard = ({
    documents,
    personId,
    canEdit,
    onRefetch,
}: {
    documents: any[];
    personId: number;
    canEdit: boolean;
    onRefetch: () => void;
}) => {
    const uploadDocuments = usePersonDocumentUpload();
    const [archiveDocument] = useMutation(ARCHIVE_PERSON_DOCUMENT);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const inputId = `person-document-${personId}`;

    const add = async (pending: PendingDocument[]) => {
        setUploading(true);
        setError('');
        try {
            const failed = await uploadDocuments(personId, pending);
            if (failed.length > 0) {
                // The reason, not just the name — otherwise every cause looks identical and
                // the only way to find out is to reproduce it by hand.
                setError(failed.map((f) => `${f.fileName}: ${f.reason}`).join(' · '));
            }
            onRefetch();
        } finally {
            setUploading(false);
        }
    };

    const remove = async (documentId: number, fileName: string) => {
        if (!window.confirm(`Remove "${fileName}"?`)) return;
        await archiveDocument({ variables: { documentId } });
        onRefetch();
    };

    return (
        <>
            {documents.length === 0 && (
                <div className={styles.emptyLine}>No supporting documents.</div>
            )}

            {documents.map((doc: any) => (
                <div className={styles.documentRow} key={doc.id}>
                    <Label basic size="tiny">{kindLabel(doc.kind)}</Label>
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className={styles.documentName}>
                        {doc.fileName || 'Document'}
                    </a>
                    <span className={styles.documentDate}>
                        {doc.createdAt ? moment(doc.createdAt).format('D MMM Y') : ''}
                    </span>
                    {canEdit && (
                        <Icon
                            name="times circle"
                            color="red"
                            link
                            onClick={() => remove(doc.id, doc.fileName || 'this document')}
                        />
                    )}
                </div>
            ))}

            {canEdit && (
                <div className={styles.documentAdd}>
                    <input
                        id={inputId}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        accept={PERSON_DOCUMENT_ACCEPT}
                        onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            if (files.length > 0) {
                                add(files.map((file) => ({ kind: 'PERMISSION_SLIP', file })));
                            }
                            e.target.value = '';
                        }}
                    />
                    <Button
                        basic
                        compact
                        size="tiny"
                        loading={uploading}
                        disabled={uploading}
                        onClick={() => document.getElementById(inputId)?.click()}
                    >
                        <Icon name="upload" /> Add document
                    </Button>
                </div>
            )}

            {error && <Message error visible size="tiny" content={error} />}
        </>
    );
};
